import { useTranslation, type TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { useStore } from "../../store/useStore"
import type {
  DiffCell as DiffCellType,
  HistoryEntry,
  PrimitiveType,
} from "../../types"
import { getFreshnessStatus } from "../../utils/freshness"

interface Props {
  cell: DiffCellType
  exportMode: boolean
  secondary?: boolean
  /** When true, the cell is visually dimmed (not matching the active diff filter) */
  faded?: boolean
  onAccept: () => void
  onEdit: (newValue: string, typeIncompatible: boolean) => void
  onDecline: () => void
  /** Full history entries for this cell's primitive (fetched lazily per-row) */
  history?: HistoryEntry[]
}

function checkTypeIncompatible(
  value: string,
  primitiveType: PrimitiveType,
): boolean {
  if (primitiveType === "string" || primitiveType === "relation") return false
  if (primitiveType === "number") {
    const cleaned = value.trim().replace(/,/g, ".")
    return isNaN(Number(cleaned))
  }
  if (primitiveType === "date") {
    return isNaN(Date.parse(value.trim()))
  }
  return false
}

// ---------------------------------------------------------------------------
// Freshness helpers
// ---------------------------------------------------------------------------

const FRESHNESS_DATE_COLOR = {
  fresh: "text-success",
  stale: "text-yellow1",
  empty: "text-red1",
} as const

/** Format an ISO timestamp as a relative time string (e.g. "3d ago", "2w ago") */
function formatRelativeTime(iso: string, translate: (key: TranslationKeys, params?: Record<string, string | number>) => string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return translate("QMS/COMMON/JUST_NOW")
  if (mins < 60) return translate("QMS/COMMON/MINS_AGO", { count: mins })
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return translate("QMS/COMMON/HOURS_AGO", { count: hrs })
  const days = Math.floor(hrs / 24)
  if (days < 14) return translate("QMS/COMMON/DAYS_AGO", { count: days })
  const weeks = Math.floor(days / 7)
  if (weeks < 8) return translate("QMS/COMMON/WEEKS_AGO", { count: weeks })
  const months = Math.floor(days / 30)
  return translate("QMS/COMMON/MONTHS_AGO", { count: months })
}

/** Shorten a source string for display (strip extension, truncate) */
function shortSource(source: string): string {
  const name = source.replace(/\.(xlsx|csv|xls|tsv)$/i, "")
  return name.length > 18 ? name.slice(0, 16) + "…" : name
}

// ---------------------------------------------------------------------------
// Change-value color: green or red depending on mode + content
// ---------------------------------------------------------------------------

function getChangeIntent(
  cell: DiffCellType,
  exportMode: boolean,
): "add" | "remove" {
  // Explicitly declined → always RED (target cell won't be touched)
  if (cell.declined) return "remove"
  if (exportMode) {
    // Per user's rule: GREEN when we're exporting into an empty document
    // cell; RED when the document cell already has content (destructive
    // override).  `rawFileValue` holds the ORIGINAL doc cell content so
    // the color is stable across user edits to `fileValue`.  Format-based
    // exports set rawFileValue=null (no source doc) → always GREEN.
    const origDocHas = cell.rawFileValue != null && cell.rawFileValue !== ""
    return origDocHas ? "remove" : "add"
  }
  // Import: RED when the DB already has content we'd overwrite, GREEN
  // when writing into an empty cell.
  const targetHas = cell.dbValue != null && cell.dbValue !== ""
  return targetHas ? "remove" : "add"
}

const CHANGE_STYLES = {
  add: {
    bg: "bg-success/10 border border-success/30",
    text: "text-success",
    btn: "bg-success/20 text-success hover:bg-success/30",
  },
  remove: {
    bg: "bg-red1/10 border border-red1/30",
    text: "text-red1",
    btn: "bg-red1/20 text-red1 hover:bg-red1/30",
  },
} as const

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Current state: freshness-colored date + DB value */
function CurrentStateSection({
  cell,
  history,
  freshnessDate,
}: {
  cell: DiffCellType
  history?: HistoryEntry[]
  freshnessDate: string | null
}) {
  const { translate } = useTranslation()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return
    function handleClick(e: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [popoverOpen])

  // Use the most recent timestamp between the lazily-fetched history and the
  // cell's own lastHistoryTimestamp (from PrimitiveValue.updated_at).  The
  // history cache may be stale (5 min staleTime) after a commit that touched
  // timestamps, while the cell data comes from freshly-refetched records.
  const latestEntry = history?.[0]
  const historyTs = latestEntry?.timestamp ?? null
  const cellTs = cell.lastHistoryTimestamp ?? null
  const lastTimestamp =
    historyTs && cellTs
      ? historyTs > cellTs
        ? historyTs
        : cellTs
      : (historyTs ?? cellTs)
  const lastSource =
    lastTimestamp === historyTs && latestEntry
      ? latestEntry.source
      : cell.lastHistorySource
  const freshness = getFreshnessStatus(lastTimestamp, freshnessDate)
  const dateColor = FRESHNESS_DATE_COLOR[freshness]
  const hasFullHistory = history && history.length > 0

  return (
    <div className="relative flex min-w-0 flex-col gap-0.5">
      {/* Freshness date line */}
      {lastTimestamp ? (
        <button
          onClick={() => setPopoverOpen(!popoverOpen)}
          className={clsx(
            "block cursor-pointer truncate text-left text-[10px] leading-tight hover:underline",
            dateColor,
          )}
          title={translate("QMS/DIFF/CLICK_VALUE_HISTORY")}
        >
          {formatRelativeTime(lastTimestamp, translate)}
          {lastSource && (
            <span className="text-grey5/70"> · {shortSource(lastSource)}</span>
          )}
        </button>
      ) : (
        <span className={clsx("text-[10px] leading-tight", dateColor)}>
          {translate("QMS/DIFF/NO_DATE")}
        </span>
      )}

      {/* DB value */}
      <span className="whitespace-pre-wrap wrap-break-word text-sm text-grey6">
        {cell.dbValue != null && cell.dbValue !== "" ? (
          cell.dbValue
        ) : (
          <span className="text-grey5">–</span>
        )}
      </span>

      {/* History popover */}
      {popoverOpen && hasFullHistory && (
        <div
          ref={popoverRef}
          className="absolute left-0 top-full z-20 mt-1 min-w-[220px] max-w-[280px] rounded-lg border border-grey4 bg-grey2 p-2.5 shadow-xl"
        >
          <p className="mb-1.5 text-[10px] font-semibold text-grey6">
            {translate("QMS/DIFF/VALUE_HISTORY")}
          </p>
          <div className="flex max-h-[140px] flex-col gap-1 overflow-y-auto">
            {history!.slice(0, 5).map((entry) => {
              const isConfirmation = entry.old_value === entry.new_value
              const ts = new Date(entry.timestamp)
              if (isConfirmation) {
                return (
                  <div
                    key={entry.id}
                    className="flex items-baseline gap-2 py-0.5 text-[10px]"
                  >
                    <span className="shrink-0 tabular-nums text-grey5">
                      {ts.toLocaleString("de-DE", {
                        dateStyle: "short",
                        timeStyle: "short",
                      })}
                    </span>
                    <span className="font-medium text-success">
                      {translate("QMS/DIFF/CONFIRMED")}
                    </span>
                  </div>
                )
              }
              return (
                <div
                  key={entry.id}
                  className="flex items-baseline gap-2 py-0.5 text-[10px]"
                >
                  <span className="shrink-0 tabular-nums text-grey5">
                    {ts.toLocaleDateString()}
                  </span>
                  <span
                    className="max-w-[90px] truncate text-grey6"
                    title={entry.new_value ?? "(empty)"}
                  >
                    {entry.new_value || (
                      <span className="italic text-grey5">{translate("QMS/DIFF/EMPTY_VALUE")}</span>
                    )}
                  </span>
                  <span
                    className="font-mono max-w-[70px] shrink-0 truncate text-grey5"
                    title={entry.source}
                  >
                    {shortSource(entry.source)}
                  </span>
                </div>
              )
            })}
          </div>
          {history!.length > 5 && (
            <p className="mt-1 text-[9px] text-grey5">
              {translate("QMS/DIFF/MORE_ENTRIES", { count: history!.length - 5 })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Change value section: shows proposed value + accept/edit/decline actions */
function ChangeValueSection({
  cell,
  exportMode,
  isResolved,
  onAccept,
  onEdit,
  onDecline,
}: {
  cell: DiffCellType
  exportMode: boolean
  isResolved: boolean
  onAccept: () => void
  onEdit: (newValue: string, typeIncompatible: boolean) => void
  onDecline: () => void
}) {
  const { translate } = useTranslation()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(cell.fileValue ?? "")
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const intent = getChangeIntent(cell, exportMode)
  const styles = CHANGE_STYLES[intent]

  // Editing inline
  if (editing) {
    const handleSave = () => {
      const incompatible = checkTypeIncompatible(draft, cell.primitiveType)
      onEdit(draft, incompatible)
      setEditing(false)
    }
    return (
      <div className="flex min-w-0 items-center gap-1">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave()
            if (e.key === "Escape") {
              setEditing(false)
              setDraft(cell.fileValue ?? "")
            }
          }}
          onBlur={handleSave}
          className="min-w-0 flex-1 rounded border border-grey5 bg-grey3 px-2 py-1 text-xs text-white focus:outline-hidden focus:ring-1 focus:ring-yellow1"
        />
      </div>
    )
  }

  // Resolved — value with hover edit + cancel icons
  if (isResolved) {
    const wasEdited = cell.dbValue != null && cell.fileValue !== cell.dbValue

    // Declined cell: show clear visual indicator that nothing will be exported
    if (cell.declined) {
      return (
        <div
          className={clsx(
            "group/resolved flex min-w-0 items-center gap-1 rounded px-1.5 py-0.5",
            CHANGE_STYLES.remove.bg,
            "opacity-80",
          )}
        >
          <span className="min-w-0 flex-1 truncate text-[10px] italic text-red1">
            {translate("QMS/DIFF/DECLINED")}
          </span>
          <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/resolved:opacity-100">
            <button
              onClick={() => {
                setDraft(cell.dbValue ?? "")
                setEditing(true)
              }}
              className="flex h-5 w-5 items-center justify-center rounded bg-grey4/50 text-grey5 transition-colors hover:bg-grey4 hover:text-grey6"
              title={translate("QMS/DIFF/EDIT_VALUE")}
            >
              <PencilIcon />
            </button>
          </span>
        </div>
      )
    }

    return (
      <div
        className={clsx(
          "group/resolved flex min-w-0 items-center gap-1 rounded px-1.5 py-0.5",
          styles.bg,
          "opacity-60",
        )}
      >
        <span
          className={clsx(
            "min-w-0 flex-1 whitespace-pre-wrap wrap-break-word text-sm",
            styles.text,
          )}
        >
          {cell.fileValue || "–"}
        </span>
        <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/resolved:opacity-100">
          <button
            onClick={() => {
              setDraft(cell.fileValue ?? "")
              setEditing(true)
            }}
            className="flex h-5 w-5 items-center justify-center rounded bg-grey4/50 text-grey5 transition-colors hover:bg-grey4 hover:text-grey6"
            title={translate("QMS/DIFF/EDIT_VALUE")}
          >
            <PencilIcon />
          </button>
          {wasEdited && (
            <button
              onClick={onDecline}
              className="flex h-5 w-5 items-center justify-center rounded bg-grey4/50 text-grey5 transition-colors hover:bg-red1/30 hover:text-red1"
              title={translate("QMS/DIFF/UNDO_CHANGE")}
            >
              <XIcon />
            </button>
          )}
        </span>
      </div>
    )
  }

  // Unresolved — value + action buttons
  return (
    <div
      className={clsx(
        "flex min-w-0 items-center gap-1 rounded px-1.5 py-0.5",
        cell.typeIncompatible
          ? "border border-dashed border-red1/50 bg-red1/10"
          : styles.bg,
      )}
    >
      <span
        className={clsx(
          "min-w-0 flex-1 whitespace-pre-wrap wrap-break-word text-sm",
          cell.typeIncompatible ? "text-red1" : styles.text,
        )}
      >
        {cell.fileValue || "–"}
      </span>

      {/* Action buttons */}
      <span className="flex shrink-0 items-center gap-0.5">
        {cell.typeIncompatible ? (
          /* Type incompatible: forced edit */
          <>
            <button
              onClick={() => {
                setDraft(cell.fileValue ?? "")
                setEditing(true)
              }}
              className="flex h-5 w-5 items-center justify-center rounded bg-red1/30 text-red1 transition-colors hover:bg-red1/50"
              title={translate("QMS/DIFF/EDIT_FIX_TYPE")}
            >
              <PencilIcon />
            </button>
            <span className="ml-0.5 text-[9px] leading-tight text-red1">
              {translate("QMS/DIFF/TYPE_ERROR_BADGE")}
            </span>
          </>
        ) : (
          <>
            {/* Accept */}
            <button
              onClick={onAccept}
              className={clsx(
                "flex h-5 w-5 items-center justify-center rounded transition-colors",
                styles.btn,
              )}
              title={translate("QMS/DIFF/ACCEPT_CHANGE")}
            >
              <CheckIcon />
            </button>
            {/* Edit */}
            <button
              onClick={() => {
                setDraft(cell.fileValue ?? "")
                setEditing(true)
              }}
              className="flex h-5 w-5 items-center justify-center rounded bg-grey4/50 text-grey5 transition-colors hover:bg-grey4 hover:text-grey6"
              title={translate("QMS/DIFF/EDIT_VALUE")}
            >
              <PencilIcon />
            </button>
            {/* Decline */}
            <button
              onClick={onDecline}
              className="flex h-5 w-5 items-center justify-center rounded bg-grey4/50 text-grey5 transition-colors hover:bg-red1/30 hover:text-red1"
              title={translate("QMS/DIFF/REJECT_CHANGE")}
            >
              <XIcon />
            </button>
          </>
        )}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function CheckIcon() {
  return (
    <svg
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  )
}

function XIcon() {
  return (
    <svg
      className="h-3 w-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Secondary inline edit — opens an input immediately, no intermediate step
// ---------------------------------------------------------------------------

function SecondaryInlineEdit({
  initialValue,
  onSave,
  onCancel,
}: {
  initialValue: string
  onSave: (value: string) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(initialValue)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  return (
    <input
      ref={inputRef}
      type="text"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSave(draft)
        if (e.key === "Escape") onCancel()
      }}
      onBlur={() => onSave(draft)}
      className="w-full rounded border border-grey5 bg-grey3 px-2 py-1 text-xs text-white focus:outline-hidden focus:ring-1 focus:ring-yellow1"
    />
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DiffCellComponent({
  cell,
  exportMode,
  faded,
  onAccept,
  onEdit,
  onDecline,
  history,
}: Props) {
  const { translate } = useTranslation()
  // Use cell-level flag: a promoted cell (cell.secondary === false) renders as primary
  // even when the scope is still secondary.
  const secondary = !!cell.secondary
  const freshnessDate = useStore((s) => s.freshnessDate)
  const isResolved = cell.accepted !== null
  // Secondary cells: local state to promote to primary layout on edit
  const [secondaryEditing, setSecondaryEditing] = useState(false)

  // Reset secondaryEditing when cell reverts from primary back to secondary (after decline)
  const prevSecondaryRef = useRef(secondary)
  useEffect(() => {
    if (secondary && !prevSecondaryRef.current) {
      setSecondaryEditing(false)
    }
    prevSecondaryRef.current = secondary
  }, [secondary])

  const currentState = (
    <CurrentStateSection
      cell={cell}
      history={history}
      freshnessDate={freshnessDate}
    />
  )

  const changeValue = (
    <ChangeValueSection
      cell={cell}
      exportMode={exportMode}
      isResolved={isResolved}
      onAccept={onAccept}
      onEdit={onEdit}
      onDecline={onDecline}
    />
  )

  // Secondary cell: default view (unresolved / resolved)
  if (secondary && !secondaryEditing) {
    // Resolved: current state + checkmark + hover edit
    if (isResolved) {
      const wasEdited = cell.fileValue !== cell.dbValue
      return (
        <div
          className={clsx(
            "group/diff flex items-center gap-1.5 transition-opacity",
            faded && "opacity-30",
          )}
        >
          <div className="min-w-0 flex-1">{currentState}</div>
          <span
            className={clsx(
              "shrink-0 text-[10px]",
              wasEdited ? "text-yellow1" : "text-success",
            )}
          >
            {wasEdited ? "✎ ✓" : "✓"}
          </span>
          <button
            onClick={() => setSecondaryEditing(true)}
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-grey4/50 text-grey5 opacity-0 transition-colors hover:bg-grey4 hover:text-grey6 group-hover/diff:opacity-100"
            title={translate("QMS/DIFF/EDIT_VALUE")}
          >
            <PencilIcon />
          </button>
        </div>
      )
    }
    // Unresolved: current state + hover actions
    // Stale cells: accept icon always visible to draw attention
    const cellFreshness = getFreshnessStatus(
      cell.lastHistoryTimestamp,
      freshnessDate,
    )
    const isStale = cellFreshness === "stale" || cellFreshness === "empty"
    return (
      <div
        className={clsx(
          "group/diff flex items-center gap-1.5 transition-opacity",
          faded && "opacity-30",
        )}
      >
        <div className="min-w-0 flex-1">{currentState}</div>
        <span className="flex shrink-0 items-center gap-0.5">
          <button
            onClick={onAccept}
            className={clsx(
              "flex h-5 w-5 items-center justify-center rounded transition-colors",
              isStale
                ? "bg-yellow1/20 text-yellow1 hover:bg-yellow1/30"
                : "bg-grey4/50 text-grey5 opacity-0 hover:bg-success/30 hover:text-success group-hover/diff:opacity-100",
            )}
            title={translate("QMS/DIFF/CONFIRM_VALUE")}
          >
            <CheckIcon />
          </button>
          <button
            onClick={() => setSecondaryEditing(true)}
            className="flex h-5 w-5 items-center justify-center rounded bg-grey4/50 text-grey5 opacity-0 transition-colors hover:bg-grey4 hover:text-grey6 group-hover/diff:opacity-100"
            title={translate("QMS/DIFF/EDIT_VALUE")}
          >
            <PencilIcon />
          </button>
        </span>
      </div>
    )
  }

  // Secondary cell: inline editing (opens input immediately, no intermediate step)
  if (secondary && secondaryEditing) {
    const handleSecondarySave = (val: string) => {
      if (val === (cell.fileValue ?? "")) {
        // No change → treat as cancel
        setSecondaryEditing(false)
        return
      }
      const incompatible = checkTypeIncompatible(val, cell.primitiveType)
      onEdit(val, incompatible) // promotes via store (secondary → false)
    }

    return (
      <div
        className={clsx(
          "group/diff flex items-start gap-2 transition-opacity",
          faded && "opacity-30",
        )}
      >
        <div className="min-w-0 max-w-[50%]">{currentState}</div>
        <div className="min-w-0 flex-1 border-l border-grey4/50 pl-2">
          <SecondaryInlineEdit
            initialValue={cell.fileValue ?? ""}
            onSave={handleSecondarySave}
            onCancel={() => setSecondaryEditing(false)}
          />
        </div>
      </div>
    )
  }

  // Primary layout (also shown for promoted secondary cells after save)
  // Promoted secondary (user edited existing DB value): import-style layout (original left, edit right)
  // In export mode, always keep export layout (change left, current right) even after edits.
  const useImportLayout =
    !exportMode &&
    cell.dbValue != null &&
    cell.fileValue != null &&
    cell.dbValue !== cell.fileValue
  return (
    <div
      className={clsx(
        "group/diff flex items-start gap-2 transition-opacity",
        faded && "opacity-30",
      )}
    >
      {exportMode && !useImportLayout ? (
        <>
          <div className="max-h-[80px] min-w-[120px] max-w-[200px] overflow-y-auto">
            {changeValue}
          </div>
          <div className="max-h-[80px] min-w-[80px] max-w-[200px] overflow-y-auto border-l border-grey4/50 pl-2">
            {currentState}
          </div>
        </>
      ) : (
        <>
          <div className="max-h-[80px] min-w-[120px] max-w-[200px] overflow-y-auto">
            {currentState}
          </div>
          <div className="max-h-[80px] min-w-[80px] max-w-[200px] overflow-y-auto border-l border-grey4/50 pl-2">
            {changeValue}
          </div>
        </>
      )}
    </div>
  )
}
